import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import { useCustomers } from '../hooks/useCustomers';
import { findDoc } from '../docs/index.ts';

interface BreadcrumbConfig {
  [path: string]: {
    label: string;
    parent?: string;
  };
}

const breadcrumbConfig: BreadcrumbConfig = {
  '/': { label: 'Home' },
  '/dashboard': { label: 'Dashboard' },
  '/profile': { label: 'Profile', parent: '/dashboard' },
  '/settings': { label: 'Settings', parent: '/dashboard' },
  '/admin': { label: 'Admin', parent: '/dashboard' },
  '/customers': { label: 'Customers' },
  '/docs': { label: 'Docs' },
  '/max-flow': { label: 'Max Flow' },
  '/backflow': { label: 'Backflow' },
  '/backflow/most-negative-avg-use': { label: 'Most Negative Avg Use', parent: '/backflow' },
  '/backflow/largest-classified': { label: 'Largest Classified', parent: '/backflow' },
};

export function NavigationBreadcrumb() {
  const location = useLocation();
  const { customers, isLoading } = useCustomers();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Check if we're on a customer page (details or properties) to fetch customer name
  const customerId = useMemo(() => {
    if (pathSegments[0] === 'customers' && pathSegments[1]) {
      return Number(pathSegments[1]);
    }
    return null;
  }, [pathSegments]);

  const customer = useMemo(() => {
    if (customerId === null || isLoading) return null;
    return customers.find((c) => c.id === customerId);
  }, [customers, customerId, isLoading]);

  // Build breadcrumb path
  const breadcrumbPath: Array<{ path: string; label: string }> = [];

  // Always start with home if not on root
  if (location.pathname !== '/') {
    breadcrumbPath.push({ path: '/', label: 'Home' });
  }

  // Build path segments
  let currentPath = '';
  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    currentPath += `/${segment}`;
    const config = breadcrumbConfig[currentPath];
    const doc = i === 1 && pathSegments[0] === 'docs' ? findDoc(segment) : undefined;

    // Special handling for customer ID in /customers/:id or /customers/:id/properties
    if (
      i === 1 &&
      pathSegments[0] === 'customers' &&
      customer
    ) {
      // Customer ID segment - link to customer details page
      breadcrumbPath.push({
        path: `/customers/${customer.id}`,
        label: customer.waterscopeAccountName,
      });
    } else if (config) {
      breadcrumbPath.push({ path: currentPath, label: config.label });
    } else if (doc) {
      // Doc slug segment - show the doc's title rather than the slug
      breadcrumbPath.push({ path: currentPath, label: doc.title });
    } else {
      // Fallback to capitalized segment name
      const label = segment.charAt(0).toUpperCase() + segment.slice(1);
      breadcrumbPath.push({ path: currentPath, label });
    }
  }

  // Don't show breadcrumb on home page or if only one item
  if (breadcrumbPath.length <= 1) {
    return null;
  }

  return (
    <Breadcrumb className='mb-4'>
      <BreadcrumbList>
        {breadcrumbPath.map((item, index) => (
          <React.Fragment key={item.path}>
            <BreadcrumbItem>
              {index === breadcrumbPath.length - 1
                ? <BreadcrumbPage>{item.label}</BreadcrumbPage>
                : <BreadcrumbLink href={item.path}>{item.label}</BreadcrumbLink>}
            </BreadcrumbItem>
            {index < breadcrumbPath.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
